import LegalLayout from "./LegalLayout";
import { config } from "../../config";

/**
 * Privacy Policy. Content is a plain-English starting point and should be
 * reviewed by counsel before publication. Update LAST_UPDATED below
 * whenever meaningful changes are made -- the date is what users see at
 * the top of the page.
 */
const LAST_UPDATED = "July 15, 2026";

export default function PrivacyPage() {
  const brand = config.brandName;
  const parent = config.parentName || "JoatLabs.dev";
  const contact = config.contactEmail || "hello@orbitcell.io";

  return (
    <LegalLayout title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <p>
        This Privacy Policy explains how {brand} collects, uses, and protects
        your information when you join our waitlist or use our marketing site.
      </p>

      <h2>Who we are</h2>
      <p>
        {brand} is operated by <strong>{parent}</strong>. The data controller
        for the waitlist is {parent}. You can reach us at{" "}
        <a href={`mailto:${contact}`}>{contact}</a>.
      </p>

      <h2>Scope</h2>
      <p>
        This policy covers the {brand} marketing site and the waitlist signup
        flow. Product application access, billing, and workspace activity are
        handled outside the public waitlist flow and may present additional
        notices as access opens or expands. We&rsquo;ll update the
        &ldquo;last updated&rdquo; date when this policy materially changes.
      </p>

      <h2>Information we collect</h2>
      <p>When you join the waitlist, you provide:</p>
      <ul>
        <li>Work email</li>
        <li>Company name</li>
        <li>
          Number of Ignition users at your company (a bucket value: 1-5, 6-25,
          26-100, 100+)
        </li>
        <li>Your role</li>
        <li>Your industry</li>
        <li>The Ignition version your team uses (8.1.x, 8.3.x, both, or other)</li>
      </ul>
      <p>We also collect automatically:</p>
      <ul>
        <li>
          Your IP address (used for rate limiting, abuse prevention, and
          approximate geographic logging &mdash; not stored against your
          identity for marketing purposes)
        </li>
        <li>Your browser&rsquo;s user-agent string</li>
        <li>
          An attribution slot recording which page section opened the form so
          we can understand which messages convert
        </li>
      </ul>
      <p>
        We do not use third-party advertising cookies. Our site analytics
        provider (Cloudflare Web Analytics) is cookieless and operates without
        persistent identifiers.
      </p>

      <h2>Why we use your information</h2>
      <ul>
        <li>To send a confirmation email after you join the waitlist</li>
        <li>To notify the {brand} team that a new signup landed</li>
        <li>To email you when early access opens</li>
        <li>To email you about pricing announcements</li>
        <li>To prevent automated abuse of the form</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your information. We do not share it
        with advertisers or data brokers. We do not use it for retargeting or
        behavioural ad networks.
      </p>

      <h2>Legal bases (GDPR)</h2>
      <p>For visitors in the EU, EEA, and UK we rely on the following bases:</p>
      <ul>
        <li>
          <strong>Consent</strong> &mdash; for sending you marketing emails
          about access and pricing. You can withdraw consent at any time using
          the unsubscribe link in any email or by emailing {contact}.
        </li>
        <li>
          <strong>Legitimate interest</strong> &mdash; for receiving and storing
          waitlist signups, understanding aggregate signup attribution, and
          preventing abuse. You can object to processing on this basis at any
          time.
        </li>
      </ul>

      <h2>Who processes data on our behalf</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> &mdash; hosts the marketing site, routes
          public requests to the OrbitCell control plane, provides the
          bot-defense service (Turnstile), and supplies cookieless web
          analytics. Cloudflare&rsquo;s processing is governed by its data
          processing agreement and standard contractual clauses for
          international transfers.
        </li>
        <li>
          <strong>OrbitCell control-plane infrastructure</strong> &mdash;
          receives waitlist submissions and stores them in the platform&rsquo;s
          access-controlled Postgres database. The retired Cloudflare D1
          database is retained temporarily for read-only migration and
          reconciliation; new submissions are not written there.
        </li>
        <li>
          <strong>Resend</strong> &mdash; delivers transactional and notification
          emails. Resend receives the email address and the body of messages
          we send.
        </li>
      </ul>
      <p>We do not sell waitlist data or share it with advertisers or data brokers.</p>

      <h2>International transfers</h2>
      <p>
        Our infrastructure and service providers may process data in the United
        States. When personal data is transferred outside your country, we rely
        on the European Commission&rsquo;s Standard Contractual Clauses (SCCs) or
        the equivalent UK addendum where required.
      </p>

      <h2>How long we keep your information</h2>
      <ul>
        <li>
          Waitlist entries are retained until {brand} launches and you either
          become a customer or are removed from the list.
        </li>
        <li>
          If you ask to be deleted, we&rsquo;ll remove your record within 30
          days, except for an audit-log entry recording that the deletion
          happened (kept for a limited period to evidence compliance).
        </li>
        <li>
          IP addresses and user-agent values associated with abuse-prevention
          logs are retained for up to 90 days.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have some or all of the following
        rights:
      </p>
      <ul>
        <li>Access &mdash; request a copy of the personal information we hold</li>
        <li>Correction &mdash; ask us to fix inaccurate information</li>
        <li>Deletion &mdash; ask us to remove your information</li>
        <li>Restriction or objection &mdash; limit how we use your information</li>
        <li>Portability &mdash; receive your data in a machine-readable format</li>
        <li>Withdraw consent &mdash; opt out of marketing emails at any time</li>
      </ul>
      <p>
        To exercise any of these rights, email <a href={`mailto:${contact}`}>{contact}</a>{" "}
        with the phrase &ldquo;data request&rdquo; in the subject line. We
        respond:
      </p>
      <ul>
        <li>
          Within 30 days for GDPR/UK GDPR requests (with up to 60 days more for
          complex requests, with notice)
        </li>
        <li>
          Within 45 days for CCPA/CPRA requests (with up to 45 days more, with
          notice)
        </li>
      </ul>
      <p>
        You also have the right to lodge a complaint with your local data
        protection authority &mdash; for example, your country&rsquo;s DPA in
        the EU/UK, or your state Attorney General in the US.
      </p>

      <h2>Children</h2>
      <p>
        The waitlist is intended for professionals making technology decisions
        for their organizations. We do not knowingly collect personal
        information from children under 16. If you believe we have, contact us
        and we&rsquo;ll delete it.
      </p>

      <h2>Security</h2>
      <p>
        We use industry-standard practices to protect your information: TLS in
        transit, encrypted storage at the database layer, limited internal
        access, and auditable access logs for administrative actions. No system
        is perfectly secure; if a breach affects your information, we will
        notify you and the relevant supervisory authorities as required by law.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We will update this policy when the public site, waitlist, or product
        access processing changes. The &ldquo;last updated&rdquo; date at the top
        reflects the most recent change. For material changes, we&rsquo;ll notify
        you by email if we already have your address.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or concerns: <a href={`mailto:${contact}`}>{contact}</a>.
      </p>
    </LegalLayout>
  );
}
